let syncFrame = 0;

const heroStates = new Map();

function getActiveDesktopSection() {
  return document.querySelector('#page-slider .splide__slide.is-active section');
}

function isLayoutVisible(layout) {
  if (!(layout instanceof Element)) {
    return true;
  }

  return layout.getClientRects().length > 0 && getComputedStyle(layout).display !== 'none';
}

function getVideoSource(video) {
  let source = video.querySelector('source');

  if (source) {
    return source;
  }

  source = document.createElement('source');
  source.type = 'video/mp4';
  video.appendChild(source);

  return source;
}

function getHeroCssNumber(section, propertyName, fallbackValue) {
  const propertyValue = getComputedStyle(section).getPropertyValue(propertyName).trim();
  const parsedValue = Number.parseFloat(propertyValue);

  if (!Number.isFinite(parsedValue)) {
    return fallbackValue;
  }

  return parsedValue;
}

function getHeroCssString(section, propertyName, fallbackValue) {
  const propertyValue = getComputedStyle(section).getPropertyValue(propertyName).trim();

  if (!propertyValue) {
    return fallbackValue;
  }

  return propertyValue.replace(/^['"]|['"]$/g, '');
}

function getHeroIntroConfig(section) {
  return {
    triggerMode: getHeroCssString(section, '--video-hero-trigger-mode', 'media-ready-or-delay'),
    siteDelayMs: getHeroCssNumber(section, '--video-hero-site-delay-ms', 500),
    mediaReadyDelayMs: getHeroCssNumber(section, '--video-hero-media-ready-delay-ms', 0),
  };
}

function setHeroMediaReady(section, isReady) {
  section.classList.toggle('video--media-ready', isReady);
}

function markSiteGateReady(section) {
  section.classList.add('video--site-gate-ready');
}

function clearStateTimer(timerId) {
  if (!timerId) {
    return 0;
  }

  window.clearTimeout(timerId);
  return 0;
}

function isHeroActive(section) {
  const video = section.querySelector('video');

  if (!video) {
    return false;
  }

  return shouldActivateVideo(video);
}

function maybeStartHeroIntro(section, state) {
  if (state.introStarted || !isHeroActive(section)) {
    return;
  }

  const config = getHeroIntroConfig(section);
  const triggerMode = config.triggerMode;
  const isReadyByMedia = state.mediaGateReady;
  const isReadyBySiteDelay = state.siteGateReady;

  let canStart;

  if (triggerMode === 'media-ready') {
    canStart = isReadyByMedia;
  } else if (triggerMode === 'site-delay') {
    canStart = isReadyBySiteDelay;
  } else {
    canStart = isReadyByMedia && isReadyBySiteDelay;
  }

  if (!canStart) {
    return;
  }

  state.introStarted = true;
  section.classList.add('video--intro-started');
}

function queueMediaGate(section, state) {
  state.mediaGateTimer = clearStateTimer(state.mediaGateTimer);

  if (!state.mediaReady) {
    state.mediaGateReady = false;
    return;
  }

  const config = getHeroIntroConfig(section);

  if (config.mediaReadyDelayMs <= 0) {
    state.mediaGateReady = true;
    maybeStartHeroIntro(section, state);
    return;
  }

  state.mediaGateReady = false;
  state.mediaGateTimer = window.setTimeout(() => {
    state.mediaGateReady = true;
    maybeStartHeroIntro(section, state);
  }, config.mediaReadyDelayMs);
}

function queueSiteGate(section, state) {
  state.siteGateTimer = clearStateTimer(state.siteGateTimer);

  const config = getHeroIntroConfig(section);

  if (config.siteDelayMs <= 0) {
    state.siteGateReady = true;
    markSiteGateReady(section);
    maybeStartHeroIntro(section, state);
    return;
  }

  state.siteGateReady = false;
  section.classList.remove('video--site-gate-ready');
  state.siteGateTimer = window.setTimeout(() => {
    state.siteGateReady = true;
    markSiteGateReady(section);
    maybeStartHeroIntro(section, state);
  }, config.siteDelayMs);
}

function handleVideoReady(section) {
  const state = heroStates.get(section);

  if (!state) {
    return;
  }

  state.mediaReady = true;
  setHeroMediaReady(section, true);
  queueMediaGate(section, state);
}

function ensureHeroState(section, video) {
  const existingState = heroStates.get(section);

  if (existingState) {
    return existingState;
  }

  const state = {
    introStarted: section.classList.contains('video--intro-started'),
    mediaReady: false,
    mediaGateReady: false,
    siteGateReady: false,
    mediaGateTimer: 0,
    siteGateTimer: 0,
    destroy: null,
  };

  const onLoadedData = () => {
    handleVideoReady(section);
  };

  video.addEventListener('loadeddata', onLoadedData);
  video.addEventListener('canplay', onLoadedData);

  state.destroy = () => {
    video.removeEventListener('loadeddata', onLoadedData);
    video.removeEventListener('canplay', onLoadedData);
    state.mediaGateTimer = clearStateTimer(state.mediaGateTimer);
    state.siteGateTimer = clearStateTimer(state.siteGateTimer);
  };

  heroStates.set(section, state);

  queueSiteGate(section, heroStates.get(section));

  if (video.readyState >= HTMLMediaElement.HAVE_CURRENT_DATA) {
    handleVideoReady(section);
  }

  return heroStates.get(section);
}

function activateVideo(video) {
  const sourceUrl = video.dataset.videoSrc;

  if (!sourceUrl) {
    return;
  }

  const source = getVideoSource(video);
  const section = video.closest('.video');

  if (!section) {
    return;
  }

  const state = ensureHeroState(section, video);

  if (source.getAttribute('src') !== sourceUrl) {
    state.mediaReady = false;
    state.mediaGateReady = false;
    setHeroMediaReady(section, false);
    source.setAttribute('src', sourceUrl);
    video.load();
  }

  if (video.readyState >= HTMLMediaElement.HAVE_CURRENT_DATA) {
    handleVideoReady(section);
  }

  maybeStartHeroIntro(section, state);

  if (video.autoplay) {
    const playResult = video.play();

    if (playResult && typeof playResult.catch === 'function') {
      playResult.catch(() => {});
    }
  }
}

function deactivateVideo(video) {
  const source = video.querySelector('source');
  const section = video.closest('.video');

  if (section) {
    const state = heroStates.get(section);

    if (state && !state.introStarted) {
      state.mediaReady = false;
      state.mediaGateReady = false;
      setHeroMediaReady(section, false);
    }
  }

  if (!source || !source.getAttribute('src')) {
    return;
  }

  video.pause();
  source.removeAttribute('src');
  video.load();
}

function shouldActivateVideo(video) {
  const layout = video.closest('.layout-desktop, .layout-mobile');

  if (!isLayoutVisible(layout)) {
    return false;
  }

  const section = video.closest('.video');

  if (!section) {
    return false;
  }

  const isDesktopSection = Boolean(video.closest('.layout-desktop'));

  if (!isDesktopSection) {
    return true;
  }

  const activeSection = getActiveDesktopSection();

  if (!activeSection) {
    return true;
  }

  return activeSection === section;
}

function syncHeroMediaNow() {
  document.querySelectorAll('.video video').forEach((video) => {
    if (shouldActivateVideo(video)) {
      activateVideo(video);
      return;
    }

    deactivateVideo(video);
  });

  heroStates.forEach((state, section) => {
    if (section.isConnected) {
      return;
    }

    state.destroy();
    heroStates.delete(section);
  });
}

export function syncHeroMedia() {
  if (syncFrame) {
    cancelAnimationFrame(syncFrame);
  }

  syncFrame = requestAnimationFrame(() => {
    syncFrame = 0;
    syncHeroMediaNow();
  });
}
